import { BaseScreenProps } from '@/features/quiz/types'
import QuizLogoHeader from '@/features/quiz/quiz-logo-header'
import React, { ReactNode, useState } from 'react'
import QuizQuestionContainer from '@/features/quiz/quiz-question-container'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  SignInScreenSchema,
  SignInScreenValues,
} from '@/features/quiz/constants'
import QuizTextInput from '@/features/quiz/inputs/quiz-text-input'
import RoundedButton from '@/shared/components/buttons/rounded-button'
import Link from 'next/link'
import { useMutation } from '@tanstack/react-query'
import useApiClient from '@/lib/apis/clients/useApiClient'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons'
import { GetSignupOptions, SignUpUser } from '@/lib/apis/authentication'
import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons'
import { login } from '@/lib/effects/authentication'
import { EMAIL_REGEX } from '@/lib/constants'
import { Effect, Either, pipe, Schedule } from 'effect'
import { translateAuthenticationError } from '@/lib/utils'
import type { AuthenticationMethod } from '@/lib/apis/types'
import SocialProviderChooser from '@/features/auth/SocialProviderChooser'
import useSocialAuthentication from '@/features/auth/useSocialAuthentication'
import {
  socialAuthenticationErrorMessage,
  SOCIAL_PROVIDER_ORDER,
} from '@/lib/auth/social-auth'
import { currentRelativePath } from '@/lib/auth/social-navigation'

export type AuthenticationScreenProps = {
  onLogin: () => Promise<void>
} & BaseScreenProps

type LoggedOutState = {
  type: 'logged-out'
}

type EmailAvailableState = {
  type: 'email-available'
}

type EmailInUseState = {
  type: 'email-in-use'
  continuationToken: string
}

// This state indicates that the user has signed up successfully and they need
// to login using the same credentials. The only difference between this and
// `email-in-use` is that with `email-in-use`, the wording shows that their
// account already exists.
type SignedUpState = {
  type: 'signed-up'
}

type SocialOnlyState = {
  type: 'social-only'
  continuationToken: string
  methods: AuthenticationMethod[]
}

type UserState =
  | LoggedOutState
  | EmailAvailableState
  | EmailInUseState
  | SignedUpState
  | SocialOnlyState

// Assume that this page will never be shown when the user is logged in
export default function QuizAuthenticationScreen({
  onLogin,
}: AuthenticationScreenProps) {
  const [userState, setUserState] = useState<UserState>({ type: 'logged-out' })
  const [passwordInputType, setPasswordInputType] = useState('password')
  const [errorMessage, setErrorMessage] = useState('')

  const client = useApiClient()

  const signupOptionsMutation = useMutation(GetSignupOptions.mutation(client))

  const signUpMutation = useMutation(SignUpUser.mutation(client))
  const socialAuthentication = useSocialAuthentication({
    onAuthenticated: onLogin,
  })

  const methods = useForm({
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    mode: 'onTouched',
    defaultValues: {
      username: '',
      password: '',
    },
    resolver: yupResolver(SignInScreenSchema),
  })

  const { handleSubmit, formState, watch } = methods
  const { isSubmitting } = formState
  const mutationPending = isSubmitting || signupOptionsMutation.isPending
  const username = watch('username')

  function startSocialAuthentication(
    provider: Parameters<typeof socialAuthentication.start>[0],
    continuationToken?: string,
  ) {
    const returnTo = currentRelativePath(window.location)
    return socialAuthentication.start(provider, {
      returnTo,
      errorTo: returnTo,
      continuationToken,
    })
  }

  async function checkEmailIsAvailable() {
    if (mutationPending || !(await methods.trigger('username'))) return

    setErrorMessage('')

    signupOptionsMutation.mutate(
      { email: username },
      {
        onSuccess: (resp) => {
          switch (resp.availability) {
            case 'NEW_ACCOUNT':
              setUserState({ type: 'email-available' })
              return
            case 'PASSWORD_ACCOUNT':
              setUserState({
                type: 'email-in-use',
                continuationToken: resp.continuationToken,
              })
              return
            case 'SOCIAL_ONLY_ACCOUNT':
              setUserState({
                type: 'social-only',
                continuationToken: resp.continuationToken,
                methods: resp.methods,
              })
              return
            case 'ROLE_CONFLICT':
              setErrorMessage(
                'This email belongs to a non-patient account. Social sign-in is only available for patients.',
              )
          }
        },
        onError: () => {
          setErrorMessage('We could not check that email. Please try again.')
        },
      },
    )
  }

  async function onSubmit(values: SignInScreenValues) {
    setErrorMessage('')

    if (userState.type === 'email-available') {
      try {
        await signUpMutation.mutateAsync({ ...values })
        setUserState({ type: 'signed-up' })
        await loginUser(values.username, values.password, true)
      } catch (err) {
        setErrorMessage(
          'Something went wrong signing you up. Please try again later.',
        )
        return
      }
    } else {
      await loginUser(values.username, values.password)
    }
  }

  async function loginUser(
    username: string,
    password: string,
    afterSignUp: boolean = false,
  ) {
    // If the user is trying to login immediately after signing up, auth0 might
    // have an issue with eventual consistency. So we need to retry a few times
    // to make sure they can login
    const loginTask = afterSignUp
      ? Effect.retry(
          login({ username, password }),
          Schedule.intersect(
            Schedule.exponential('100 millis'),
            Schedule.recurs(5),
          ),
        )
      : login({ username, password })

    const task = Effect.gen(function* ($) {
      const result = yield* pipe(
        loginTask,
        Effect.andThen(() => Effect.tryPromise(() => onLogin())),
        Effect.either,
      )

      if (Either.isRight(result)) {
        return
      }

      const err = result.left

      // If the user is trying to login immediately after signing up, we show
      // a different error to ask them to try again.
      if (afterSignUp) {
        setErrorMessage(
          'Sign up was successful, but we were unable to log you in. Please try again later with the same email and password you used to sign up. If you continue to have issues, please contact us at support@nextclinic.com.au.',
        )
        return
      }

      switch (err._tag) {
        case 'AuthenticationError':
          setErrorMessage(translateAuthenticationError(err.type))
          return

        case 'NetworkError':
          setErrorMessage(translateAuthenticationError('NETWORK_ERROR'))
          return

        case 'UnknownException':
          setErrorMessage(
            'An unexpected error happened. Please refresh the page and try again.',
          )
          return
      }
    })

    await Effect.runPromise(task)
  }

  function layout(children: ReactNode) {
    return (
      <>
        <QuizLogoHeader />
        <QuizQuestionContainer
          header={{
            title: 'Get the treatment you need.',
            subtitle:
              'See your personalized treatment options. Private and 100% online.',
          }}
        >
          <div className="-mt-4">
            <FormProvider {...methods}>
              <form
                className="flex-flex-col gap-y-3"
                onSubmit={(event) => {
                  if (userState.type === 'logged-out') {
                    event.preventDefault()
                    void checkEmailIsAvailable()
                    return
                  }

                  if (userState.type === 'social-only') {
                    event.preventDefault()
                    return
                  }

                  return handleSubmit(onSubmit)(event)
                }}
              >
                {children}
              </form>
            </FormProvider>
          </div>
          <p className="mx-auto mt-40 max-w-xs text-pretty text-center text-sm text-gray-600 sm:max-w-sm">
            By creating an account using email, I agree to the{' '}
            <Link
              href="/page/terms-of-service"
              target="_blank"
              className="text-sm text-gray-500 underline"
            >
              Terms & Conditions
            </Link>{' '}
            and acknowledge the{' '}
            <Link
              href="/page/privacy-policy"
              target="_blank"
              className="text-sm text-gray-500 underline"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </QuizQuestionContainer>
      </>
    )
  }

  if (userState.type === 'logged-out') {
    // Using the error from `formState` is not very reliable because it doesn't
    // validate on mount.
    const emailIsValid = EMAIL_REGEX.test(username)

    return layout(
      <div className="flex flex-col gap-y-5">
        <QuizTextInput
          name="username"
          label="Email"
          disabled={mutationPending}
          hasError={!!errorMessage}
        />
        <SimpleErrorMessage
          message={
            socialAuthentication.error
              ? socialAuthenticationErrorMessage(socialAuthentication.error)
              : errorMessage
          }
        />
        <RoundedButton
          type="submit"
          disabled={!emailIsValid}
          loading={mutationPending}
        >
          Continue
        </RoundedButton>
        <SocialProviderChooser
          action="sign-in"
          onSelect={(provider) => startSocialAuthentication(provider)}
          pendingProvider={socialAuthentication.pendingProvider}
          variant="quiz"
        />
      </div>,
    )
  }

  if (userState.type === 'social-only') {
    const socialError = socialAuthentication.error
      ? socialAuthenticationErrorMessage(socialAuthentication.error)
      : ''

    return layout(
      <div className="flex flex-col gap-y-5">
        <QuizTextInput name="username" label="Email" disabled />
        <p className="text-sm leading-5 text-gray-600">
          This account uses social sign-in. Choose a linked method to continue
          without losing your answers.
        </p>
        <SimpleErrorMessage message={socialError || errorMessage} />
        <SocialProviderChooser
          action="sign-in"
          onSelect={(provider) =>
            startSocialAuthentication(provider, userState.continuationToken)
          }
          pendingProvider={socialAuthentication.pendingProvider}
          providers={SOCIAL_PROVIDER_ORDER.filter((provider) =>
            userState.methods.some(
              (method) =>
                method.type === provider && method.linked && method.configured,
            ),
          )}
          showDivider={false}
          variant="quiz"
        />
        <button
          className="text-sm font-semibold text-blue-700"
          onClick={() => setUserState({ type: 'logged-out' })}
          type="button"
        >
          ← Use another email
        </button>
      </div>,
    )
  }

  const socialError = socialAuthentication.error
    ? socialAuthenticationErrorMessage(socialAuthentication.error)
    : ''

  return layout(
    <div className="flex flex-col gap-y-5">
      <QuizTextInput name="username" label="Email" hasError={!!errorMessage} />
      <QuizTextInput
        type={passwordInputType}
        name="password"
        label="Password"
        autoFocus={true}
        hasError={!!errorMessage}
        contentAtEnd={
          <span
            className="cursor-pointer text-gray-600"
            onClick={() => {
              if (passwordInputType === 'password') {
                setPasswordInputType('text')
              } else {
                setPasswordInputType('password')
              }
            }}
          >
            <FontAwesomeIcon
              icon={passwordInputType === 'password' ? faEye : faEyeSlash}
            />
          </span>
        }
      />
      <SimpleErrorMessage message={socialError || errorMessage} />
      {userState.type === 'email-in-use' && (
        <p className="mt-2 pl-4 text-sm font-medium text-gray-600">
          <FontAwesomeIcon icon={faCircleExclamation} className="mr-1.5" />
          Account with your email exists, please login.
        </p>
      )}
      <RoundedButton loading={isSubmitting} type="submit">
        Continue
      </RoundedButton>
      {userState.type === 'email-in-use' && (
        // Keep focus in the password field while these are clicked. Blurring
        // the empty field renders its validation error above, which shifts
        // the link and buttons under the pointer before the click completes.
        <div
          className="flex flex-col gap-y-5"
          onMouseDown={(event) => event.preventDefault()}
        >
          <Link
            href="/forgot-password"
            target="_blank"
            rel="noopener"
            className="text-center text-sm font-semibold text-blue-700"
          >
            Forgotten your password?
          </Link>
          <SocialProviderChooser
            action="sign-in"
            disabled={isSubmitting}
            onSelect={(provider) =>
              startSocialAuthentication(provider, userState.continuationToken)
            }
            pendingProvider={socialAuthentication.pendingProvider}
            variant="quiz"
          />
        </div>
      )}
    </div>,
  )
}

type ErrorProps = {
  message: string
}

function SimpleErrorMessage({ message }: ErrorProps) {
  if (!message) return null

  return (
    <div className="relative mt-3 block pl-4 text-sm text-red-600">
      <FontAwesomeIcon icon={faCircleExclamation} className="mr-1.5" />
      {`${message}`}
    </div>
  )
}
